export const BANNER_STATUS = {
  LIVE: 'live',
  SCHEDULED: 'scheduled',
  PAUSED: 'paused',
  STALE: 'stale',
  // Honest "no real desk status yet" state — rendered when the desk-status/freshness endpoints have
  // returned no channels and no feeds, so MarginBanner / CockpitPage render a neutral empty state
  // instead of fabricated numbers.
  UNAVAILABLE: 'unavailable',
};

// GseFeedKind (Interfirst.Skynet.Pricing) — Base = Freddie Cash asset-price feed, Srp = the servicer
// SRP grid feeds. StringEnumConverter serializes these as their name.
export const GSE_FEED_KIND = {
  BASE: 'Base',
  SRP: 'Srp',
};

// GsePullStatus (Interfirst.Skynet.Pricing) — every state a durable GsePricingPull can hold. Cockpit's
// suspect-pull review queue reads Quarantined; the Feeds & Sources pull history (LEN-88) reads all of
// them (one GET gse-pricing/pulls?status= per state, merged).
export const GSE_PULL_STATUS = {
  RAW: 'Raw',
  VALIDATED: 'Validated',
  QUOTABLE: 'Quotable',
  QUARANTINED: 'Quarantined',
  HARD_REJECT: 'HardReject',
};

// The held-pulls review list's page size, and the server's largest page (the desk status reads one
// page that big so its per-reason counts cover more than the newest few holds).
export const GSE_HELD_PULLS_PAGE_SIZE = 50;

export const GSE_PULLS_MAX_PAGE_SIZE = 200;

export const GSE_PULL_REVIEW_DECISION = {
  PROMOTE: 'Promote',
  DISCARD: 'Discard',
};

// Hold-reason codes other than the anomaly rules (C1-C9). MARKET_CLOSED leads when every cause is
// Freddie being closed (nights, weekends) — an expected hold, not a problem.
export const GSE_HOLD_REASON_CODE = {
  MARKET_CLOSED: 'MarketClosed',
  FETCH_ERROR: 'FetchError',
  COMPLETENESS: 'Completeness',
};

// The reads the Feeds & Sources "Recent pulls" history fans out over, newest-quality first. Kept as
// an explicit list (not Object.values) so the request order is deterministic and Quotable — the pulls
// that actually produced pricing — leads. The pulls endpoint defaults to the pending review queue
// (market-closed holds and reviewed holds left out, 50 rows), so the history asks for market-closed
// holds, the largest page, and a second Quarantined read for the holds a person already discarded.
const GSE_PULL_HISTORY_PAGE = { includeMarketClosed: true, pageSize: GSE_PULLS_MAX_PAGE_SIZE };

export const GSE_PULL_HISTORY_QUERIES = [
  { status: GSE_PULL_STATUS.QUOTABLE, ...GSE_PULL_HISTORY_PAGE },
  { status: GSE_PULL_STATUS.VALIDATED, ...GSE_PULL_HISTORY_PAGE },
  { status: GSE_PULL_STATUS.RAW, ...GSE_PULL_HISTORY_PAGE },
  { status: GSE_PULL_STATUS.QUARANTINED, ...GSE_PULL_HISTORY_PAGE },
  { status: GSE_PULL_STATUS.QUARANTINED, reviewed: true, ...GSE_PULL_HISTORY_PAGE },
  { status: GSE_PULL_STATUS.HARD_REJECT, ...GSE_PULL_HISTORY_PAGE },
];

// Human labels + MarginChip tones per GsePullStatus. Quotable = servable (fern/green), Validated =
// in-flight-clean (slate), Raw = just landed (grey), Quarantined = held for review (amber), HardReject
// = rejected (crimson).
export const GSE_PULL_STATUS_LABELS = {
  [GSE_PULL_STATUS.RAW]: 'Raw',
  [GSE_PULL_STATUS.VALIDATED]: 'Validated',
  [GSE_PULL_STATUS.QUOTABLE]: 'Quotable',
  [GSE_PULL_STATUS.QUARANTINED]: 'Quarantined',
  [GSE_PULL_STATUS.HARD_REJECT]: 'Hard reject',
};

export const GSE_PULL_STATUS_TONE = {
  [GSE_PULL_STATUS.RAW]: 'grey',
  [GSE_PULL_STATUS.VALIDATED]: 'slate',
  [GSE_PULL_STATUS.QUOTABLE]: 'fern',
  [GSE_PULL_STATUS.QUARANTINED]: 'amber',
  [GSE_PULL_STATUS.HARD_REJECT]: 'crimson',
};

// Hover explanations for the pull outcomes that never reach pricing. Only Quotable pulls are served
// (GsePullStateMachine), so a held or rejected pull leaves the prior Quotable pull pricing.
export const GSE_PULL_STATUS_HINTS = {
  [GSE_PULL_STATUS.QUARANTINED]:
    'Failed a completeness or anomaly check, so it isn’t used for pricing. The last good pull keeps serving.',
  [GSE_PULL_STATUS.HARD_REJECT]:
    'Unreadable response, so it isn’t used for pricing. The last good pull keeps serving.',
};

// GsePriceType (Interfirst.Skynet.Pricing) — the Freddie feed each pull came from. Cash is the base
// asset-price feed; the two XChange types are servicer SRP-grid feeds.
export const GSE_PRICE_TYPE_LABELS = {
  Cash: 'Cash',
  CashReleasedXChange: 'Cash-Released XChange',
  CoIssueXChange: 'Co-Issue XChange',
};

export const GSE_CASH_PRICE_TYPE = 'Cash';

// The feeds whose pulls are servicer SRP grids (no rate sheet) — they open the SRP explorer.
export const GSE_SRP_FEED_PRICE_TYPES = ['CashReleasedXChange', 'CoIssueXChange'];

// Honest empty Feeds & Sources pull-data shape (LEN-88) — what the feedPulls slice resolves to before
// the freshness/pulls reads return, on error, or when the company has no feeds/pulls yet. Never a
// fabricated feed or pull row.
export const EMPTY_FEED_PULLS = {
  feeds: [],
  pulls: [],
};

// The per-pull grid drill-down (LEN-88) — the selected "Recent pulls" row's priced grid. pullId tracks
// which row is open so the drawer can render the row's own metadata while the grid loads; data is the
// GsePricingPullGrid response (null until loaded).
export const EMPTY_PULL_GRID = {
  pullId: null,
  data: null,
  isLoading: false,
  errorMessage: null,
};

// The SRP-feed pull's note-rate x loan-amount matrix (LEN-105) — data is the GsePricingPullSrpMatrix
// response, kept while a filter change refetches so the explorer never blanks between selections.
export const EMPTY_PULL_SRP_MATRIX = {
  pullId: null,
  requestId: null,
  data: null,
  isLoading: false,
  errorMessage: null,
};

// Data is kept while a product / execution change refetches so the spec-pool explorer never blanks between
// selections.
export const EMPTY_PULL_SPEC_POOLS = {
  pullId: null,
  requestId: null,
  data: null,
  isLoading: false,
  errorMessage: null,
};

// Honest empty desk-status shape — the merged view MC-8's getDeskStatusAction produces when the
// desk-status (MC-3) / freshness (MC-4) / quarantine (MC-5) endpoints return nothing (unavailable,
// 403/404, or genuinely empty for this company). MarginBanner and CockpitPage render honest empty
// states off this shape instead of a fabricated sheet version / publisher / channel list / reprice log.
export const EMPTY_DESK_STATUS = {
  bannerStatus: null,
  primaryChannelName: null,
  sheetTitle: null,
  primaryEffective: null,
  publishedBy: null,
  publishedAt: null,
  baseAgeMinutes: null,
  baseStale: false,
  srpAge: null,
  srpStale: false,
  channels: [],
  // MC-9 (deferred): no reprice-log source endpoint exists yet — stays honestly empty until it lands.
  repriceLog: [],
  quarantine: [],
  // Every held pull pending review, not just the page read (null when the server total is unreadable).
  quarantineTotal: null,
  isQuarantineTotalLowerBound: false,
  // Raw Base/SRP feed entries (one per Seller/PriceType) — kept alongside the flattened banner/tile
  // figures so other screens (e.g. RulesPage's sample-price attribution) can anchor a live
  // GsePricingPullId without re-fetching freshness themselves.
  freshnessFeeds: [],
};

// Margin Center MC-7 (DEV-4969): honest empty preview shape — what `marginCenter.preview.data`
// resolves to before a draft plan is selected, while loading, or on error (graceful degrade — never a
// fabricated diff). DEV-4977/MC-14 adds the three coarseFloatingPipeline* summary fields — null (not 0)
// means "not computed yet / Loans unreachable", never a fabricated zero.
export const EMPTY_MARGIN_PREVIEW = {
  companyId: null,
  marginPlanId: null,
  channelId: null,
  noteRates: [],
  cellDiffs: [],
  heatmap: { noteRates: [], rows: [] },
  curveSeries: [],
  summary: {
    cellsChanged: 0,
    cellsBelowFloor: 0,
    cellsAboveCeiling: 0,
    cellsWithCurveInversion: 0,
    maxAbsoluteDeviationBps: 0,
    totalFloatingPipelineDollarImpact: 0,
    hasBlockingIssues: false,
    coarseFloatingPipelineLoanCount: null,
    coarseFloatingPipelineLoanAmount: null,
    coarseFloatingPipelineDollarImpact: null,
  },
};

export const PRICE_SOURCE_KIND = {
  RATE_SHEET: 'RateSheet',
};

export const PRICE_SOURCE_SERVICING = {
  RELEASED: 'Released',
  RETAINED: 'Retained',
};

export const PRICE_SOURCE_FRESHNESS_MODE = {
  END_OF_DAY: 'EndOfDay',
  MAX_AGE: 'MaxAge',
};

export const PRICE_SOURCE_CHECK_SEVERITY = {
  HARD: 'Hard',
  SOFT: 'Soft',
};

const PRICE_SOURCE_LOCK_DAY = {
  TEN: 10,
  FIFTEEN: 15,
  TWENTY_ONE: 21,
  THIRTY: 30,
  FORTY_FIVE: 45,
  SIXTY: 60,
  SEVENTY_FIVE: 75,
  NINETY: 90,
  ONE_HUNDRED_TWENTY: 120,
};

export const PRICE_SOURCE_LOCK_DAYS = Object.values(PRICE_SOURCE_LOCK_DAY);

// Pre-checked for a new product. Pricing only needs at least one allowed lock day per product,
// because many investor sheets carry no 15/30/45/60 column.
export const PRICE_SOURCE_DEFAULT_LOCK_DAYS = [
  PRICE_SOURCE_LOCK_DAY.FIFTEEN,
  PRICE_SOURCE_LOCK_DAY.THIRTY,
  PRICE_SOURCE_LOCK_DAY.FORTY_FIVE,
  PRICE_SOURCE_LOCK_DAY.SIXTY,
];

export const PRICE_SOURCE_MAX_AGE_HOURS_CEILING = 168;

export const PRICE_SOURCE_MAX_PRODUCTS = 100;

// The stable `code` Pricing puts on a price-source or upload 409 body. Only a stale proposalVersion
// (ProposalChanged) sends the user back to review; the rest need a reload.
export const UPLOAD_CONFLICT_CODE = {
  PROPOSAL_CHANGED: 'ProposalChanged',
  AI_OVER_MANUAL: 'AiOverManual',
  BASED_ON_VERSION_MISMATCH: 'BasedOnVersionMismatch',
  BASED_ON_VERSION_REQUIRED: 'BasedOnVersionRequired',
  DUPLICATE_UPLOAD: 'DuplicateUpload',
  UPLOAD_NOT_EDITABLE: 'UploadNotEditable',
  ALREADY_PUBLISHED: 'AlreadyPublished',
  UPLOAD_CHANGED: 'UploadChanged',
  PROPOSAL_INCOMPLETE: 'ProposalIncomplete',
  NO_QUOTABLE_VERSION: 'NoQuotableVersion',
  VERSION_NOT_REVIEWABLE: 'VersionNotReviewable',
  READING_UNRESOLVED: 'ReadingUnresolved',
};

// A person's decision on a Quarantined rate-sheet version (PriceSourceVersionSummary.reviewDecision).
export const PRICE_SOURCE_VERSION_REVIEW_DECISION = {
  PROMOTED: 'Promoted',
  DISCARDED: 'Discarded',
};

// A Pricing build without `code` on its 409s is matched on this exact message instead
// (PriceSourceUploadWorkflow.LabelsChanged).
export const UPLOAD_LABELS_CHANGED_MESSAGE = 'The labels changed since you reviewed them';

export const PRICE_SOURCE_UPLOAD_STATUS = {
  NEEDS_LABELS: 'NeedsLabels',
  READY_FOR_REVIEW: 'ReadyForReview',
  AWAITING_SECOND_APPROVAL: 'AwaitingSecondApproval',
  PUBLISHED: 'Published',
  FAILED: 'Failed',
  DISCARDED: 'Discarded',
  AI_READING: 'AiReading',
};

// PriceSourceReadingMode (LEN-259 phase 6): how a source's rate-sheet uploads are read. A source with
// no readingMode setting reads the legacy way. ClosedSet (phase 7.2): Pricing splits every tab into
// blocks first, the AI answers each block, and the products each grid applies to come from the
// sheet's headings.
export const PRICE_SOURCE_READING_MODE = {
  LEGACY: 'Legacy',
  AI_FIRST: 'AiFirst',
  CLOSED_SET: 'ClosedSet',
};

// BlockDisposition: what a closed-set reading answered a block is.
export const BLOCK_DISPOSITION = {
  PRICE: 'Price',
  LLPA: 'Llpa',
  FEATURE: 'Feature',
  SRP: 'Srp',
  LOCK: 'Lock',
  ELIGIBILITY: 'Eligibility',
  NOT_PRICING: 'NotPricing',
};

// NotPricingReason: why a block holds no prices.
export const NOT_PRICING_REASON = {
  ELIGIBILITY: 'Eligibility',
  FEES: 'Fees',
  LOCK_EXTENSION: 'LockExtension',
  PRICE_CAP: 'PriceCap',
  MORTGAGE_INSURANCE: 'MortgageInsurance',
  MARGINS: 'Margins',
  LOAN_LIMITS: 'LoanLimits',
  REQUIREMENTS: 'Requirements',
  CONTACT: 'Contact',
  CALCULATOR: 'Calculator',
  DUPLICATE: 'Duplicate',
  RATE_ADDER: 'RateAdder',
  OTHER: 'Other',
};

// The closed program vocabulary a label's tag is chosen from (ProgramFamily, ProgramQualifier).
export const PROGRAM_FAMILY = {
  CONVENTIONAL: 'Conventional',
  FHA: 'Fha',
  VA: 'Va',
  USDA: 'Usda',
  GOVERNMENT: 'Government',
  JUMBO: 'Jumbo',
  NON_QM: 'NonQm',
  DSCR: 'Dscr',
  ITIN: 'Itin',
  FOREIGN_NATIONAL: 'ForeignNational',
  CLOSED_END_SECOND: 'ClosedEndSecond',
  HELOC: 'Heloc',
  OTHER: 'Other',
};

export const PROGRAM_QUALIFIER = {
  FNMA: 'Fnma',
  FHLMC: 'Fhlmc',
  STANDARD: 'Standard',
  HIGH_BALANCE: 'HighBalance',
  STREAMLINE: 'Streamline',
  A_PLUS: 'APlus',
  A: 'A',
  A_MINUS: 'AMinus',
  B: 'B',
};

// ScopeSource: where an adjustment grid's products came from.
export const SCOPE_SOURCE = {
  AI: 'Ai',
  DERIVED: 'Derived',
  PERSON: 'Person',
  TEMPLATE: 'Template',
};

// CoverageClass: how the coverage map classes a numeric or label cell of a closed-set tab.
export const COVERAGE_CLASS = {
  READ: 'Read',
  NOT_PRICING: 'NotPricing',
  UNCOVERED: 'Uncovered',
  LABEL: 'Label',
};

// NeedsPersonReason: why a closed-set reading's item waits for a person before it can be published.
export const NEEDS_PERSON_REASON = {
  EMPTY_SCOPE: 'EmptyScope',
  UNTAGGED_BLOCK: 'UntaggedBlock',
  AMBIGUOUS_LOCK_LINK: 'AmbiguousLockLink',
  NO_PRODUCT_TERM: 'NoProductTerm',
  PREFIXED_ROW: 'PrefixedRow',
  TERM_CONDITION_DISAGREES: 'TermConditionDisagrees',
  UNCLOSED_TAB: 'UnclosedTab',
};

// PriceSourceTemplateStatus: a reading-template version is Proposed by an upload, Active once that
// upload is saved with its template, then Superseded by the next one, or Rejected with its upload.
export const PRICE_SOURCE_TEMPLATE_STATUS = {
  PROPOSED: 'Proposed',
  ACTIVE: 'Active',
  SUPERSEDED: 'Superseded',
  REJECTED: 'Rejected',
};

// UploadTemplateMatch.grade on an AiFirst upload: how far the file drifted from the reading template.
export const TEMPLATE_DRIFT_GRADE = {
  IDENTICAL: 0,
  MINOR: 1,
  STRUCTURAL: 2,
  NEW_LAYOUT: 3,
};

export const UPLOAD_GRID_KIND = {
  PRICE: 'Price',
  LLPA: 'Llpa',
  IGNORE: 'Ignore',
  FEATURE: 'Feature',
  SRP: 'Srp',
  ELIGIBILITY_MATRIX: 'EligibilityMatrix',
};

export const STAGED_GRID_KIND = {
  PRICE: 'Price',
  LLPA: 'Llpa',
  UNKNOWN: 'Unknown',
  LOCK_ADJUSTMENT: 'LockAdjustment',
  SRP: 'Srp',
  ELIGIBILITY_MATRIX: 'EligibilityMatrix',
};

export const UPLOAD_TYPE = {
  RATE_SHEET: 'RateSheet',
  MATRIX: 'Matrix',
};

export const STAGED_SHEET_NOTE_KIND = {
  MAX_PRICE: 'MaxPrice',
  MIN_PRICE: 'MinPrice',
};

export const PRICE_CAP_SOURCE = {
  SHEET: 'Sheet',
  MANUAL: 'Manual',
};

export const PRICE_CAP_LIMITS = {
  LOWEST: 50,
  HIGHEST: 150,
};

export const MATRIX_VERSION_STATUS = {
  ACTIVE: 'Active',
  SUPERSEDED: 'Superseded',
  DEACTIVATED: 'Deactivated',
};

// Review (phase 7.2): "worth a look". Shown apart from Blocking, it neither blocks auto-publish nor
// counts as Blocking.
export const UPLOAD_WARNING_SEVERITY = {
  INFO: 'Info',
  BLOCKING: 'Blocking',
  REVIEW: 'Review',
};

export const UPLOAD_PROPOSAL_SOURCE = {
  TEMPLATE: 'Template',
  AI: 'Ai',
  MANUAL: 'Manual',
};

export const PRICE_CONVENTION = {
  PRICE: 'Price',
  POINTS: 'Points',
};

export const LLPA_FIELD = {
  FICO: 'Fico',
  LTV: 'Ltv',
  CLTV: 'Cltv',
  LOAN_AMOUNT: 'LoanAmount',
  OCCUPANCY: 'Occupancy',
  PROPERTY_TYPE: 'PropertyType',
  PURPOSE: 'Purpose',
  UNITS: 'Units',
  STATE: 'State',
  HIGH_BALANCE: 'HighBalance',
  TERM_MONTHS: 'TermMonths',
  DOC_TYPE: 'DocType',
  DSCR: 'Dscr',
  INTEREST_ONLY: 'InterestOnly',
  PREPAY_TERM_MONTHS: 'PrepayTermMonths',
  PREPAY_TYPE: 'PrepayType',
  DTI: 'Dti',
  ESCROW_WAIVER: 'EscrowWaiver',
  CITIZENSHIP: 'Citizenship',
  FIRST_TIME_BUYER: 'FirstTimeBuyer',
  FEATURE: 'Feature',
  CREDIT_EVENT_SEASONING_MONTHS: 'CreditEventSeasoningMonths',
  NOTE_RATE: 'NoteRate',
};

export const LLPA_NUMERIC_FIELDS = [
  LLPA_FIELD.FICO,
  LLPA_FIELD.LTV,
  LLPA_FIELD.CLTV,
  LLPA_FIELD.LOAN_AMOUNT,
  LLPA_FIELD.UNITS,
  LLPA_FIELD.TERM_MONTHS,
  LLPA_FIELD.DSCR,
  LLPA_FIELD.PREPAY_TERM_MONTHS,
  LLPA_FIELD.DTI,
  LLPA_FIELD.NOTE_RATE,
];

// The rows of a Feature grid (Pricing's FeatureCode).
export const FEATURE_CODE = {
  INTEREST_ONLY: 'InterestOnly',
  ESCROW_WAIVER: 'EscrowWaiver',
  FIRST_TIME_BUYER: 'FirstTimeBuyer',
  FOREIGN_NATIONAL: 'ForeignNational',
  ITIN: 'Itin',
  NON_PERMANENT_RESIDENT: 'NonPermanentResident',
  ANY_CREDIT_EVENT: 'AnyCreditEvent',
  BANKRUPTCY: 'Bankruptcy',
  FORECLOSURE: 'Foreclosure',
  SHORT_SALE_OR_DEED_IN_LIEU: 'ShortSaleOrDeedInLieu',
  CREDIT_EVENT_WITHIN_12_MONTHS: 'CreditEventWithin12Months',
  CREDIT_EVENT_WITHIN_24_MONTHS: 'CreditEventWithin24Months',
  CREDIT_EVENT_WITHIN_36_MONTHS: 'CreditEventWithin36Months',
  CREDIT_EVENT_WITHIN_48_MONTHS: 'CreditEventWithin48Months',
  SHORT_TERM_RENTAL: 'ShortTermRental',
  NON_WARRANTABLE_CONDO: 'NonWarrantableCondo',
  CONDOTEL: 'Condotel',
  RURAL: 'Rural',
  MIXED_USE: 'MixedUse',
  TEMPORARY_BUYDOWN: 'TemporaryBuydown',
  BUYDOWN_21: 'Buydown21',
  BUYDOWN_11: 'Buydown11',
  BUYDOWN_321: 'Buydown321',
  DOC_TYPE_ALT: 'DocTypeAlt',
  NON_OWNER_OCCUPIED: 'NonOwnerOccupied',
  UNITS_2_TO_4: 'Units2To4',
  RENOVATION_203K: 'Renovation203K',
  SECTION_184: 'Section184',
  SECTION_247: 'Section247',
  SIMPLE_REFI: 'SimpleRefi',
  STREAMLINE: 'Streamline',
  LLPA_WAIVER: 'LlpaWaiver',
  HOME_READY: 'HomeReady',
  HOME_POSSIBLE: 'HomePossible',
  REFI_NOW: 'RefiNow',
  REFI_POSSIBLE: 'RefiPossible',
  HOME_STYLE_RENOVATION: 'HomeStyleRenovation',
  CHOICE_RENOVATION: 'ChoiceRenovation',
  MANUFACTURED_HOME: 'ManufacturedHome',
  SUBORDINATE_FINANCING: 'SubordinateFinancing',
  MANUAL_UNDERWRITING: 'ManualUnderwriting',
  HIGH_BALANCE: 'HighBalance',
  CASH_OUT: 'CashOut',
  CONDO: 'Condo',
  SECOND_HOME: 'SecondHome',
  INVESTMENT_PROPERTY: 'InvestmentProperty',
  LENDER_PAID_MI: 'LenderPaidMi',
  NO_CREDIT_SCORE: 'NoCreditScore',
};

export const MATRIX_METRIC = {
  LTV: 'Ltv',
  LTV_AND_CLTV: 'LtvAndCltv',
};

export const LLPA_UNIT = {
  POINTS: 'Points',
  BPS: 'Bps',
};

export const LLPA_SIGN = {
  COST_POSITIVE: 'CostPositive',
  CREDIT_POSITIVE: 'CreditPositive',
};

export const SRP_SIGN = {
  GAIN_POSITIVE: 'GainPositive',
  COST_POSITIVE: 'CostPositive',
};

export const UPLOAD_DIFF_STATUS = {
  CHANGED: 'Changed',
  UNCHANGED: 'Unchanged',
  ADDED: 'Added',
  MISSING: 'Missing',
  REMOVED: 'Removed',
};

// Settings keys Pricing keeps as they are when a PUT leaves them out. A form that doesn't edit them
// omits them, so a stale copy never overwrites a template an upload learned or adjustments saved meanwhile.
export const PRICE_SOURCE_SETTING_KEY = {
  ADJUSTMENTS: 'adjustments',
  TEMPLATE: 'template',
};

export const DEFAULT_AUTO_PUBLISH_SETTINGS = {
  enabled: false,
  maxMoveBps: 25,
  secondApproverAboveBps: 100,
};

const BYTES_PER_MEGABYTE = 1048576;

const PRICE_SOURCE_UPLOAD_MAX_MEGABYTES = 25;

export const PRICE_SOURCE_UPLOAD_MAX_BYTES = PRICE_SOURCE_UPLOAD_MAX_MEGABYTES * BYTES_PER_MEGABYTE;

// Matches the AI labelling process's proposal limit; Pricing itself only refuses an exact
// (program, product) duplicate.
export const RATE_SHEET_MAX_PROGRAMS_PER_PRODUCT = 3;
